import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import axios from 'axios';
import { config } from '../constants/config'


// CharSpeechSample node properties ------------------------------
// <id>: 55
// enabled: true
// textSnippetUuid: "14d9ff48-16c9-44c0-92d3-1dd8810cefec"
// title: "A Test Sample Title"
// uuid: "14d9ff48-16c9-44c0-92d3-1dd8810cefec"
// voiceSnippetUuid: "14d9ff48-16c9-44c0-92d3-1dd8810cefec"

// Text Snippet node properties ----------------------------------
// <id>: 113
// createdAt: 2023-07-18T05:21:11.899000000Z
// displayText: "Uninitialized"
// fileName: "AnyBoy.txt"
// s3TextUrl: "https://aai-api.s3.us-west-1.amazonaws.com/aai-users/91355c8b-c056-4861-979c-dc8bb4a1e1bf/text-sample-snippets/AnyBoy.txt"
// uuid: "14d9ff48-16c9-44c0-92d3-1dd8810cefec"
// variation: "happy"
// version: 1.0

// Voice Snippet node properties ----------------------------------
// <id>: 89
// createdAt: 2023-07-30T19:29:59.755000000Z
// fileName: "Tosha.wav"
// s3Url: "https://aai-api.s3.us-west-1.amazonaws.com/aai-users/91355c8b-c056-4861-979c-dc8bb4a1e1bf/voice-clone-snippets/Tosha.wav"
// uuid: "14d9ff48-16c9-44c0-92d3-1dd8810cefec"
// variation: "Excited"
// version: 1.0


export const fetchAllSamples = createAsyncThunk(
  'charSamples/fetchAllSamples',
  async ({ userId }) => {
    let query = `${config.GET_API_ALL_SAMPLES}/${userId}`
    // console.log('GET IT NOW: charSamples/fetchAllSamples')
    const response = await axios.get(query);
    return response.data;
  }
);

export const fetchSample = createAsyncThunk(
  'charSamples/fetchSample',
  async ({ userId, csUuid }) => {
    let query = `${config.GET_API_SAMPLE}/${userId}/${csUuid}`
    const response = await axios.get(query);
    return response.data;
  }
);

export const deleteCharacterSpeechSample = createAsyncThunk(
  'charSamples/deleteCharacterSpeechSample',
  async ({ userId, targetUuid }) => {
    const response = await axios.delete(`${config.DELETE_API_SAMPLE}/${targetUuid}`);
    return response.data;
  }
);

export const deleteSampleDetails = createAsyncThunk(
  'charSamples/deleteSampleDetails',
  async ({ userId, csUuid }) => {
    const response = await axios.delete(`${config.DELETE_API_SAMPLE_DETAILS}/${userId}/${csUuid}`);
    return response.data;
  }
);

export const fetchAllCharacterSpeechSamples = createAsyncThunk(
  'charSamples/fetchAllCharacterSpeechSamples',
  async ({ userId }) => {
    let query = `${config.GET_API_ALL_CHAR_SPEECH_SAMPLES}/${userId}`
    const response = await axios.get(query);
    return response.data;
  }
);


export const addCharacterSpeechSample = createAsyncThunk(
  'userLitGrpTree/addVariationToBookCharacter', // Corrected action name to match function's purpose
  async ({ userId,
    title,
    lit_group_uuid,
    book_uuid,
    char_speech_uuid,
    character_uuid,
    variation_uuid,
    variation_name,
    textSnippet_uuid,
    voiceSnippet_uuid,
    revision }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      // In axios.post, the URL is the first parameter, the data (body) is the second parameter, and the config is the third parameter.
      const response = await axios.post(config.ADD_CHARACTER_SPEECH_SAMPLE, {
        userId,
        title,
        lit_group_uuid,
        book_uuid,
        char_speech_uuid,
        character_uuid,
        variation_uuid,
        variation_name,
        textSnippet_uuid,
        voiceSnippet_uuid,
        revision
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

// Add an empty character speech sample
export const addEmptyCharacterSpeechSample = createAsyncThunk(
  'charSamples/addEmptyCharacterSpeechSample',
  async ({ userId, litGroup, bookData, charUuid, variationUuid, variationName }, { dispatch, rejectWithValue }) => {
    console.log('CALLED addEmptyCharacterSpeechSample variationUuid', variationUuid)
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(config.ADD_EMPTY_CHARACTER_SPEECH_SAMPLE, {
        userId,
        title: "Describe sample",
        lit_group_uuid: litGroup,
        book_uuid: bookData.bookTree.uuid,
        char_speech_uuid: uuidv4(),
        character_uuid: charUuid,
        variation_uuid: variationUuid,
        variation_name: variationName,
        textSnippet_uuid: "",
        voiceSnippet_uuid: "",
        revision: 1
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      // After successfully adding a new sample, fetch all character speech samples to refresh the state
      await dispatch(fetchAllCharacterSpeechSamples({ userId }));
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const removeCharacterVariation = createAsyncThunk(
  'charSamples/removeCharacterVariation',
  async ({ characterUuid, variationUuid }) => {
    const response = await axios.post(config.REMOVE_VARIATION_FROM_BOOK_CHARACTER, {
      characterUuid,
      variationUuid
    });
    return response.data;
  }
);

export const getHighestRevision = createAsyncThunk(
  'charSamples/getHighestRevision',
  async ({ userId, litGroup, bookData, charUuid, variationUuid }) => {
    const response = await axios.get(config.GET_HIGHEST_REVISION, {
      params: {
        userId,
        lit_group_uuid: litGroup,
        book_uuid: bookData.bookTree.uuid,
        character_uuid: charUuid,
        variation_uuid: variationUuid,
      }
    });
    return response.data;
  }
);

export const getTextSnippetOverride = createAsyncThunk(
  'charSamples/getTextSnippetOverride',
  async ({ userId, uuid }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${config.GET_API_TEXT_SNIPPET_OVERRIDE}/${userId}/${uuid}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'An error occurred');
    }
  }
);

export const addTextSnippetOverrideThunk = createAsyncThunk(
  'charSamples/addTextSnippetOverrideThunk',
  async ({ userId, uuid, textContent, version = 1 }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(config.ADD_API_TEXT_SNIPPET_OVERRIDE, {
        userId,
        uuid,
        textContent,
        version,
        isActive: true
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'An error occurred');
    }
  }
);

// Keeping the old thunk for backward compatibility
export const addTextSnippetOverride = addTextSnippetOverrideThunk;
const findCharSpeechByUuid = (uuid, samplesList) => {
  // Check if samplesTree is an object and has the 'samples' key
  if (_.isArray(samplesList) && samplesList.length > 0) {
    // Iterate over the 'samples' array
    for (const sample of samplesList) {
      // Check if this sample is a 'CharSpeechSample' and has the matching UUID
      if (sample._type === 'CharSpeechSample' && sample.uuid === uuid) {
        return sample;
      }
    }
  }
  return null; // Return null if no matching CharSpeechSample is found
};

const charSamplesSlice = createSlice({
  name: 'charSamples',
  initialState: {
    charSamples: [],
    charSpeechSamples: [],
    status: 'idle',
    deleteSampleStatus: 'idle',
    deleteSampleDetailsStatus: 'idle',
    fetchAllSamplesStatus: 'idle',
    fetchAllCharacterSpeechSamplesStatus: 'idle',
    fetchSampleStatus: 'idle',
    addEmptyCharacterSpeechSampleStatus: 'idle',
    removeCharacterVariationStatus: 'idle',
    addTextSnippetOverrideStatus: 'idle',
    getTextSnippetOverrideStatus: 'idle',
    textSnippetOverrides: [], // Changed to array to store all overrides
    textSnippetOverride: null, // Keep for backward compatibility
    newEmptyCharacterSpeechData: null, // Add this to track the new empty sample data
    triggerUpdate: 0, // Add this line
    error: null,
  },
  reducers: {
    updateDisplayText: (state, action) => {
      const { textSnippetUuid, displayText } = action.payload;
      let allSamples = _.cloneDeep(state.charSamples.samples.sampleList.samples);
      const sampleIndex = allSamples.findIndex(sample => sample.textSnippetUuid === textSnippetUuid);

      if (sampleIndex !== -1) {
        allSamples[sampleIndex].textSnippet.displayText = displayText;
        state.charSamples.samples.sampleList.samples = allSamples;
        console.log('after update samples:', state.charSamples.samples.sampleList.samples); // Log the updated displayText

      } else {
        console.log('Sample not found for given textSnippetUuid:', textSnippetUuid); // Log if the sample is not found
      }
    },
    triggerReRender: (state) => { // Add this reducer
      state.triggerUpdate += 1;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllSamples.pending, (state) => {
        state.fetchAllSamplesStatus = 'loading';
      })
      .addCase(fetchAllSamples.fulfilled, (state, action) => {
        state.fetchAllSamplesStatus = 'succeeded';
        state.charSamples = action.payload;
      })
      .addCase(fetchAllSamples.rejected, (state, action) => {
        state.fetchAllSamplesStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchSample.pending, (state) => {
        state.fetchSampleStatus = 'loading';
      })
      .addCase(fetchSample.fulfilled, (state, action) => {
        state.fetchSampleStatus = 'succeeded';
        state.charSamples = action.payload;
      })
      .addCase(fetchSample.rejected, (state, action) => {
        state.fetchSampleStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(deleteCharacterSpeechSample.pending, (state) => {
        state.deleteSampleStatus = 'deleting';
      })
      .addCase(deleteCharacterSpeechSample.fulfilled, (state, action) => {
        state.deleteSampleStatus = 'succeeded';
        state.samples = action.payload;
      })
      .addCase(deleteCharacterSpeechSample.rejected, (state, action) => {
        state.deleteSampleStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchAllCharacterSpeechSamples.pending, (state) => {
        state.fetchAllCharacterSpeechSamplesStatus = 'loading';
      })
      .addCase(fetchAllCharacterSpeechSamples.fulfilled, (state, action) => {
        state.fetchAllCharacterSpeechSamplesStatus = 'succeeded';
        state.charSpeechSamples = action.payload;
      })
      .addCase(fetchAllCharacterSpeechSamples.rejected, (state, action) => {
        state.fetchAllCharacterSpeechSamplesStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(addEmptyCharacterSpeechSample.pending, (state) => {
        state.addEmptyCharacterSpeechSampleStatus = 'loading';
      })
      .addCase(addEmptyCharacterSpeechSample.fulfilled, (state, action) => {
        state.addEmptyCharacterSpeechSampleStatus = 'succeeded';
        state.newEmptyCharacterSpeechData = action.payload; // Store the new sample data
      })
      .addCase(addEmptyCharacterSpeechSample.rejected, (state, action) => {
        state.addEmptyCharacterSpeechSampleStatus = 'failed';
        state.error = action.payload ? action.payload : action.error.message;
      })
      .addCase(removeCharacterVariation.pending, (state) => {
        state.removeCharacterVariationStatus = 'loading';
      })
      .addCase(removeCharacterVariation.fulfilled, (state, action) => {
        state.removeCharacterVariationStatus = 'succeeded';
        state.triggerUpdate += 1; // Trigger re-render
      })
      .addCase(removeCharacterVariation.rejected, (state, action) => {
        state.removeCharacterVariationStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(addTextSnippetOverrideThunk.pending, (state) => {
        state.addTextSnippetOverrideStatus = 'loading';
      })
      .addCase(addTextSnippetOverrideThunk.fulfilled, (state, action) => {
        state.addTextSnippetOverrideStatus = 'succeeded';
        // If we need to update any state with the response data, we would do it here
        state.triggerUpdate += 1; // Trigger re-render
      })
      .addCase(addTextSnippetOverrideThunk.rejected, (state, action) => {
        state.addTextSnippetOverrideStatus = 'failed';
        state.error = action.payload ? action.payload : action.error.message;
      })
      .addCase(getTextSnippetOverride.pending, (state) => {
        state.getTextSnippetOverrideStatus = 'loading';
        state.textSnippetOverrides = [];
        state.textSnippetOverride = null;
      })
      .addCase(getTextSnippetOverride.fulfilled, (state, action) => {
        state.getTextSnippetOverrideStatus = 'succeeded';

        // Store the array of overrides if it exists
        if (action.payload.textOverrides && Array.isArray(action.payload.textOverrides)) {
          state.textSnippetOverrides = action.payload.textOverrides;

          // Find the active override or the most recent one
          const activeOverride = action.payload.textOverrides.find(override => override.isActive) ||
            (action.payload.textOverrides.length > 0 ? action.payload.textOverrides[action.payload.textOverrides.length - 1] : null);

          // For backward compatibility
          state.textSnippetOverride = activeOverride;
        } else if (action.payload.textOverride) {
          // Handle the case where a single override is returned
          state.textSnippetOverrides = [action.payload.textOverride];
          state.textSnippetOverride = action.payload.textOverride;
        } else {
          state.textSnippetOverrides = [];
          state.textSnippetOverride = null;
        }
      })
      .addCase(getTextSnippetOverride.rejected, (state, action) => {
        state.getTextSnippetOverrideStatus = 'failed';
        state.textSnippetOverrides = [];
        state.textSnippetOverride = null;
        state.error = action.payload ? action.payload : action.error.message;
      });
  },
});


export default charSamplesSlice.reducer;
export const { updateDisplayText, triggerReRender } = charSamplesSlice.actions;
export const selectAddTextSnippetOverrideStatus = (state) => state?.user?.charSamples?.addTextSnippetOverrideStatus || 'idle';
export const selectGetTextSnippetOverrideStatus = (state) => state?.user?.charSamples?.getTextSnippetOverrideStatus || 'idle';
export const selectTextSnippetOverride = (state) => state?.user?.charSamples?.textSnippetOverride;
// Stable empty-array fallback — a fresh `[]` per call makes useSelector warn
// ("returned a different result … same parameters") and rerender needlessly.
const EMPTY_ARRAY = Object.freeze([]);
export const selectTextSnippetOverrides = (state) => state?.user?.charSamples?.textSnippetOverrides || EMPTY_ARRAY;
export const selectActiveTextSnippetOverride = (state) => {
  const overrides = state?.user?.charSamples?.textSnippetOverrides || [];
  // First try to find an override with isActive=true
  const activeOverride = overrides.find(override => override?.isActive);
  if (activeOverride) return activeOverride;

  // If no active override is found, return the most recent one (last in the array)
  return overrides.length > 0 ? overrides[overrides.length - 1] : null;
};

export const selectTriggerUpdate = (state) => state?.user?.charSamples?.triggerUpdate || 0;
export const selectAddEmptyCharacterSpeechSampleStatus = (state) => {
  let addStatus = state?.user?.charSamples?.addEmptyCharacterSpeechSampleStatus || 'idle';
  return addStatus;
}
export const selectNewEmptyCharacterSpeechData = (state) => {
  let newSpeechData = state?.user?.charSamples?.newEmptyCharacterSpeechData;
  return newSpeechData;
}
export const selectSamplesStatus = (state) => state?.user?.charSamples?.fetchAllSamplesStatus || "idle";
export const selectSampleStatus = (state) => state?.user?.charSamples?.fetchSampleStatus || "idle";
export const getAllSamples = (state) => state?.user?.charSamples?.charSamples?.samples?.sampleList?.samples || [];
export const getAllCharSpeechSamples = (state) => state?.user?.charSamples?.charSpeechSamples?.samples || [];
export const getLitGroupsForUser = (state) => state?.user?.charSamples?.charSpeechSamples?.litGroups || [];


const getCharBookGrpList = (CSCample) => {
  let charListOptions = []
  if (CSCample && CSCample.charBookGroupLists) {
    charListOptions = CSCample.charBookGroupLists.map((x) => {
      let key_value = x.name.replace(/[^a-z0-9]/gi, '').toLowerCase()
      return { key: key_value, text: x.name, value: key_value, data: x }
    })
    return charListOptions;
  }
  return [{ key: 'emptyList', text: 'Empty Character List', value: 'emptyList', data: {} }]
}

const getBooksList = (bkOptionFromSample) => {
  let bookListOption = []
  if (bkOptionFromSample.book) {
    let bk = bkOptionFromSample.book
    let key_value = bk.name.replace(/[^a-z0-9]/gi, '').toLowerCase()
    let shortName = bk.name.replace(' - Book', '');
    let bkOption = { key: bk.name.replace(/[^a-z0-9]/gi, '').toLowerCase(), text: shortName, value: key_value, data: bk }
    bookListOption.push(bkOption)
    return bookListOption;
  }
  return [{ key: 'emptyBookList', text: 'Empty Book List', value: 'emptyBookList', data: {} }]

}


const getLitGroupList = (litGroupList) => {
  let litGroupOptions = []
  if (litGroupList) {
    litGroupOptions = litGroupList.map((x) => {
      let key_value = x.name.replace(/[^a-z0-9]/gi, '').toLowerCase()
      let shortName = x.name.replace(' - Characters', '');
      shortName = shortName.replace(' Characters', '');
      return { key: key_value, text: shortName, value: key_value, data: x }
    })
    return litGroupOptions;
  }
  return [{ key: 'emptyGroupList', text: 'Empty Literaty Group List', value: 'emptyGroupList', data: {} }]

}

const findBookFromSample = (booksAndSamples, sampleUuid) => {
  let foundBook = []
  _.forEach(booksAndSamples, book => {
    _.forEach(book.sampleList, sample => {
      if (sample.uuid === sampleUuid) {
        console.log("FOUND BOOK FOR SAMPLE", book)
        foundBook = book;
        return false;
      }
    })
    if (!_.isEmpty(foundBook)) return false;
  })
  return foundBook;
}

const getCharListForBook = (bookUuid, bookCharacter) => {
  let bookCharList = []
  _.forEach(bookCharacter, bkItem => {
    if (bkItem.uuid === bookUuid) {
      bookCharList = bkItem.characterList;
      return false
    }
  })
  return bookCharList
}

export const getLitGroupDefaultList = (state) => {
  let litGroupOptionsList = getLitGroupList(state?.user?.charSamples?.charSpeechSamples?.litGroups)
  // console.log('litGroupOptionsList', litGroupOptionsList)
  return litGroupOptionsList;
}

export const getCharSpeechSampleByUuid = (state, cloneState) => {
  let allCharSpeechSamples = [];
  let allBookSamples = [];
  let bookWithSample = []
  let bookCharacter = []

  let activeCSLists = { ltGroupOptions: [], bookOptions: [], charOptions: [], bookFullImage: {} }

  allCharSpeechSamples = state?.user?.charSamples?.charSpeechSamples?.samples || [];
  allBookSamples = state?.user?.charSamples?.charSpeechSamples?.booksAndSamples || [];
  bookCharacter = state?.user?.charSamples?.charSpeechSamples?.booksAndCharacters || [];
  bookWithSample = findBookFromSample(allBookSamples, cloneState.uuid)
  let bkWithSampleAndCharacters = { ...bookWithSample, characterList: getCharListForBook(bookWithSample.uuid, bookCharacter) }
  activeCSLists.bookFullImage = bkWithSampleAndCharacters;

  let bookList = getBooksList(bkWithSampleAndCharacters)

  console.log('bookWithSample + charList', bkWithSampleAndCharacters, 'bookListOptions', bookList)
  activeCSLists.ltGroupOptions = getLitGroupList(state?.user?.charSamples?.charSpeechSamples?.litGroups)

  if (allCharSpeechSamples.length < 1) {
    console.log('GET ALL CHAR SPEECH SAMPLES === NO SAMPLES')
  }
  else {
    allCharSpeechSamples = findCharSpeechByUuid(cloneState.uuid, allCharSpeechSamples);
    activeCSLists.charOptions = getCharBookGrpList(allCharSpeechSamples)
    activeCSLists.bookOptions = bookList; //getCSBooksList(allCharSpeechSamples)
    activeCSLists.ltGroupOptions = getLitGroupList(state?.user?.charSamples?.charSpeechSamples?.litGroups)
    console.log('getCharSpeechSampleByUuid', allCharSpeechSamples, 'uuid', cloneState.uuid, 'foundSample', activeCSLists, 'bookCharacter', bkWithSampleAndCharacters)
  }
  return activeCSLists;
}


// set default options.
export const getInitialCharSpeechSample = (state) => {
  // let allCharSpeechSamples = [];
  let bookOptions = [{ key: 'noBookOptions', text: 'No Books', value: 'No Books', data: {} }];
  let charOptions = [{ key: 'noCharOptions', text: 'No Characters', value: 'No Characters', data: {} }];
  let ltGroupOptions = [{ key: 'noLitGroups', text: 'No Literary Groups', value: 'No Literary Groups', data: {} }];
  let activeCSLists = { ltGroupOptions: ltGroupOptions, bookOptions: bookOptions, charOptions: charOptions, bookFullImage: {} }
  return activeCSLists;
}
